import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  ArrowRightIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import './styles/CommandPalette.css';

const RECENT_KEY = 'commandPaletteRecent';
const MAX_RECENT = 5;

const CommandPalette = ({ isOpen, onClose, navigationItems = [], isDarkMode, primaryColor = '#1E3A5F' }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Load recent items from localStorage
  const getRecent = () => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch { return []; }
  };

  const saveRecent = (item) => {
    const recent = getRecent().filter(r => r.href !== item.href);
    recent.unshift({ name: item.name, href: item.href });
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  };

  // Filter items based on query
  const filtered = query.trim()
    ? navigationItems.filter((item) => {
        const q = query.toLowerCase();
        return (
          item.name.toLowerCase().includes(q) ||
          item.href.toLowerCase().includes(q) ||
          (item.module || '').toLowerCase().includes(q)
        );
      })
    : navigationItems;

  // Recent items (show only when no query)
  const recentItems = !query.trim()
    ? getRecent()
        .map((r) => navigationItems.find((n) => n.href === r.href))
        .filter(Boolean)
    : [];

  // Combined list: recent first (if no query), then all/filtered
  const displayItems = !query.trim() && recentItems.length > 0
    ? [
        ...recentItems,
        ...filtered.filter((item) => !recentItems.some((r) => r.href === item.href)),
      ]
    : filtered;

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const item = listRef.current.children[selectedIndex];
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Navigate to selected item
  const handleSelect = useCallback((item) => {
    if (!item) return;
    saveRecent(item);
    navigate(item.href);
    onClose();
  }, [navigate, onClose]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, displayItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (displayItems[selectedIndex]) {
          handleSelect(displayItems[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  };

  // Global ⌘K / Ctrl+K listener
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) {
          onClose();
        } else {
          // Trigger open via parent
          window.dispatchEvent(new CustomEvent('openCommandPalette'));
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Highlight matching text
  const highlightMatch = (text) => {
    if (!query.trim()) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="cp-highlight" style={{ color: primaryColor, fontWeight: 600 }}>
          {text.slice(idx, idx + query.length)}
        </span>
        {text.slice(idx + query.length)}
      </>
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="cp-overlay" onClick={onClose}>
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: -20 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className={`cp-container ${isDarkMode ? 'cp-dark' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="cp-input-wrapper">
            <MagnifyingGlassIcon className="cp-input-icon" />
            <input
              ref={inputRef}
              type="text"
              className="cp-input"
              placeholder="Search modules, navigate anywhere..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              spellCheck={false}
            />
            <kbd className="cp-esc-badge">ESC</kbd>
          </div>

          {/* Results */}
          <div className="cp-results" ref={listRef}>
            {/* Recent header */}
            {!query.trim() && recentItems.length > 0 && (
              <div className="cp-section-label">
                <ClockIcon className="cp-section-icon" />
                Recent
              </div>
            )}

            {/* Recent items */}
            {!query.trim() && recentItems.map((item, i) => {
              const Icon = item.icon;
              const isSelected = selectedIndex === i;
              return (
                <button
                  key={`recent-${item.href}`}
                  className={`cp-item ${isSelected ? 'cp-item-selected' : ''}`}
                  style={isSelected ? { backgroundColor: `${primaryColor}10` } : {}}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setSelectedIndex(i)}
                >
                  <div className="cp-item-icon-wrap" style={isSelected ? { color: primaryColor } : {}}>
                    {Icon && <Icon className="cp-item-icon" />}
                  </div>
                  <span className="cp-item-name">{item.name}</span>
                  <span className="cp-item-path">{item.href}</span>
                  {isSelected && <ArrowRightIcon className="cp-item-arrow" style={{ color: primaryColor }} />}
                </button>
              );
            })}

            {/* All / Filtered header */}
            {!query.trim() && recentItems.length > 0 && (
              <div className="cp-section-label">All Modules</div>
            )}
            {query.trim() && displayItems.length > 0 && (
              <div className="cp-section-label">Results</div>
            )}

            {/* Filtered items */}
            {displayItems
              .filter((item) => query.trim() || !recentItems.some((r) => r.href === item.href))
              .map((item) => {
                const globalIdx = displayItems.indexOf(item);
                const Icon = item.icon;
                const isSelected = selectedIndex === globalIdx;
                return (
                  <button
                    key={item.href}
                    className={`cp-item ${isSelected ? 'cp-item-selected' : ''}`}
                    style={isSelected ? { backgroundColor: `${primaryColor}10` } : {}}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setSelectedIndex(globalIdx)}
                  >
                    <div className="cp-item-icon-wrap" style={isSelected ? { color: primaryColor } : {}}>
                      {Icon && <Icon className="cp-item-icon" />}
                    </div>
                    <span className="cp-item-name">{highlightMatch(item.name)}</span>
                    <span className="cp-item-path">{item.href}</span>
                    {isSelected && <ArrowRightIcon className="cp-item-arrow" style={{ color: primaryColor }} />}
                  </button>
                );
              })}

            {/* No results */}
            {displayItems.length === 0 && (
              <div className="cp-empty">
                <MagnifyingGlassIcon className="cp-empty-icon" />
                <p className="cp-empty-text">No modules found for "{query}"</p>
                <p className="cp-empty-hint">Try a different search term</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="cp-footer">
            <div className="cp-footer-hint">
              <kbd className="cp-kbd">↑↓</kbd>
              <span>Navigate</span>
            </div>
            <div className="cp-footer-hint">
              <kbd className="cp-kbd">↵</kbd>
              <span>Open</span>
            </div>
            <div className="cp-footer-hint">
              <kbd className="cp-kbd">esc</kbd>
              <span>Close</span>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CommandPalette;
